import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { tWithKeyAttr as t } from '../../../i18nHelper';
import styles from './dashboardLayout.module.css';
import DashboardNavTabs, { DashboardTab } from '../dashboardNavTabs/dashboardNavTabs';
import { useAppSelector } from '../../../services/hooks';
import { adminSelector } from '../../../services/selectors/user.selectors';
import { AppImage } from '../../appMedia';

interface DashboardLayoutProps {
  useCustomHeader?: boolean;
}

const dashboardTabs: DashboardTab[] = [
  { path: '/dashboards/daily', labelKey: 'DAILY_MANAGEMENT', iconClass: 'iconDaily' },
  { path: '/dashboards/financial', labelKey: 'FINANCIAL', iconClass: 'iconFinancial' },
  { path: '/dashboards/clinical', labelKey: 'CLINICAL', iconClass: 'iconClinical' },
  { path: '/dashboards/sales', labelKey: 'SALES', iconClass: 'iconSales' },
  { path: '/dashboards/therapists', labelKey: 'THERAPISTS', iconClass: 'iconTherapists' },
  { path: '/dashboards/patients', labelKey: 'PATIENTS', iconClass: 'iconPatients', usesMockData: true },
  { path: '/dashboards/burnout', labelKey: 'BURNOUT', iconClass: 'iconBurnout', usesMockData: true },
  { path: '/dashboards/questionnaire', labelKey: 'QUESTIONNAIRE_STATISTICS', iconClass: 'iconQuestionnaire' },
  { path: '/dashboards/crisis-hotline', labelKey: 'CRISIS_HOTLINE_INSIGHTS', iconClass: 'iconCrisisHotline' },
];

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ useCustomHeader = true }) => {
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const admin = useAppSelector(adminSelector);
  const companyLogoUrl = admin?.companyLogoUrl;
  const companyName = admin?.selectedCompany?.CompanyName || admin?.companyName || 'bpreven';

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [companyLogoUrl]);

  if (!useCustomHeader) {
    // Fallback: render children without custom header (for future flexibility)
    return (
      <div className={styles.legacyContainer}>
        <Outlet />
      </div>
    );
  }

  return (
    <div className={styles.dashboardWrapper}>
      {/* Custom Dashboard Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logo}>
            {companyLogoUrl && !logoLoadFailed ? (
              <AppImage
                className={styles.companyLogoImg}
                src={companyLogoUrl}
                alt={t('COMPANY_LOGO')}
                onError={(e) => {
                  console.log('[DashboardLayout] Company logo failed to load, using fallback. Failed URL:', e.currentTarget.src);
                  setLogoLoadFailed(true);
                }}
              />
            ) : (
              <>
                <div className={styles.logoIcon}>🏥</div>
                <span className={styles.logoText}>{companyName}</span>
              </>
            )}
          </div>
          <DashboardNavTabs tabs={dashboardTabs} />
        </div>
      </header>

      {/* Dashboard Content */}
      <main className={styles.dashboardContent}>
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
